import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LANDING_URL, paths } from '@shared/config';
import {
  dismissInstallPromotion,
  getInstallPlatform,
  promptPwaInstall,
  usePwaInstall,
  type InstallPlatform,
} from '@shared/pwa/install';
import { Button, SegPills, type SegPillOption } from '@shared/ui';
import './InstallPage.css';

const PLATFORMS: SegPillOption<InstallPlatform>[] = [
  { key: 'ios', label: 'iPhone и iPad' },
  { key: 'android', label: 'Android' },
  { key: 'desktop', label: 'Компьютер' },
];

// Small inline copies of the browser controls the steps refer to.
const glyph = (shape: ReactNode) => (
  <span className="pwa-glyph" aria-hidden="true">
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {shape}
    </svg>
  </span>
);

const SHARE = glyph(
  <>
    <path d="M8 9H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1" />
    <path d="M12 3v12M8.5 6.5 12 3l3.5 3.5" />
  </>,
);
const ADD = glyph(
  <>
    <rect x="4" y="4" width="16" height="16" rx="4" />
    <path d="M12 8.5v7M8.5 12h7" />
  </>,
);
const MENU = glyph(
  <>
    <circle cx="12" cy="5.5" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="12" cy="18.5" r="1.3" fill="currentColor" stroke="none" />
  </>,
);
const INSTALL = glyph(
  <>
    <rect x="3" y="4" width="18" height="13" rx="2" />
    <path d="M12 7.5v6M9.5 11l2.5 2.5 2.5-2.5M8 20.5h8" />
  </>,
);

const STEPS: Record<InstallPlatform, ReactNode[]> = {
  ios: [
    <>
      Открой эту страницу в <strong>Safari</strong>.
    </>,
    <>
      Нажми <strong>«Поделиться»</strong> {SHARE} на панели Safari или в меню «•••».
    </>,
    <>
      Выбери <strong>«На экран „Домой“»</strong> {ADD}.
    </>,
    <>
      Нажми <strong>«Добавить»</strong>. Если есть переключатель «Открывать как веб-приложение», включи его.
    </>,
  ],
  android: [
    <>Открой меню браузера {MENU}.</>,
    <>
      Выбери <strong>«Установить приложение»</strong> или <strong>«Добавить на главный экран»</strong>.
    </>,
    <>Подтверди установку — значок Lomora появится на главном экране.</>,
  ],
  desktop: [
    <>
      Открой эту страницу в <strong>Chrome</strong> или <strong>Edge</strong>.
    </>,
    <>
      Нажми значок установки {INSTALL} в адресной строке или выбери в меню {MENU}{' '}
      <strong>«Установить Lomora»</strong>.
    </>,
    <>Подтверди установку — Lomora откроется в отдельном окне.</>,
  ],
};

function HomeScreen({ installed }: { installed: boolean }) {
  return (
    <div className="pwa-home" aria-hidden="true">
      <span className="pwa-home__tile" />
      <span className="pwa-home__tile" />
      <span className="pwa-home__app">
        <img src="/icons/contented-fox/pwa/icon-192x192.png" alt="" width="64" height="64" />
        {installed && (
          <span className="pwa-home__check">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="m5 12.5 4.5 4.5L19 7.5" />
            </svg>
          </span>
        )}
        <span className="pwa-home__label">Lomora</span>
      </span>
      <span className="pwa-home__tile" />
      <span className="pwa-home__tile" />
    </div>
  );
}

function InstallLink({ platform }: { platform: InstallPlatform }) {
  const [copied, setCopied] = useState(false);
  const url = `${location.origin}${paths.install}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // The address stays visible for manual copying.
    }
  };

  return (
    <div className="pwa-install__link">
      <p>
        {platform === 'desktop'
          ? 'Чтобы установить на телефон, открой эту ссылку на нём:'
          : `Нет такого пункта? Открой ссылку в ${platform === 'ios' ? 'Safari' : 'Chrome'}: из Telegram, VK и других приложений установить не получится.`}
      </p>
      <div className="pwa-install__url">
        <span>{`${location.host}${paths.install}`}</span>
        <button type="button" onClick={copy}>
          {copied ? 'Скопировано' : 'Скопировать'}
        </button>
      </div>
    </div>
  );
}

export default function InstallPage() {
  const { available, installed } = usePwaInstall();
  const [platform, setPlatform] = useState(getInstallPlatform);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  // Открыли из меню приложения (?from=app): вверху «Назад» в приложение, а не «На сайт».
  const [searchParams] = useSearchParams();
  const fromApp = searchParams.get('from') === 'app';
  const navigate = useNavigate();
  const location = useLocation();
  const back = () => (location.key !== 'default' ? navigate(-1) : navigate(paths.dashboard));

  const install = async () => {
    setPending(true);
    const result = await promptPwaInstall();
    setPending(false);
    if (result === 'accepted') {
      setMessage('Устанавливаем — значок Lomora появится на главном экране.');
    } else if (result === 'dismissed') {
      dismissInstallPromotion();
      setMessage('Установка отменена. Можно вернуться к ней позже или продолжить в браузере.');
    } else {
      setMessage('Окно установки не открылось. Установи через меню браузера — шаги ниже.');
    }
  };

  const manual = (
    <>
      <SegPills className="pwa-install__platforms" options={PLATFORMS} value={platform} onChange={setPlatform} />
      <ol className="pwa-install__steps">
        {STEPS[platform].map((step, index) => (
          <li key={`${platform}-${index}`}>{step}</li>
        ))}
      </ol>
      <InstallLink platform={platform} />
    </>
  );

  return (
    <main className="pwa-install">
      <div className="pwa-install__content">
        {fromApp ? (
          <button type="button" className="pwa-install__site" onClick={back}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
            Назад
          </button>
        ) : (
          <a className="pwa-install__site" href={LANDING_URL} onClick={dismissInstallPromotion}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
            На сайт
          </a>
        )}

        <section className="pwa-install__hero" aria-labelledby="install-title">
          <HomeScreen installed={installed} />
          <h1 id="install-title">{installed ? 'Lomora уже на главном экране' : 'Lomora на главном экране'}</h1>
          <p className="pwa-install__intro">
            {installed
              ? 'Открывай карточки и тренировки через значок приложения.'
              : 'Одно касание — и ты в своих карточках. Приложение открывается во весь экран, без вкладок браузера.'}
          </p>
          {!installed && (
            <ul className="pwa-install__facts">
              <li>Бесплатно</li>
              <li>Без App Store и Google Play</li>
            </ul>
          )}
        </section>

        {installed ? (
          <Link className="pwa-install__open" to={paths.dashboard}>
            Открыть Lomora
          </Link>
        ) : available ? (
          <>
            <Button fullWidth size="lg" disabled={pending} onClick={install}>
              {pending ? 'Открываем установку…' : 'Установить'}
            </Button>
            <details className="pwa-install__manual">
              <summary>Установить через меню браузера</summary>
              {manual}
            </details>
          </>
        ) : (
          <section className="pwa-install__manual" aria-labelledby="install-steps-title">
            <h2 id="install-steps-title">Как установить</h2>
            {manual}
          </section>
        )}

        <p className="pwa-install__status" role="status">
          {message}
        </p>

        {!installed && (
          <Link className="pwa-install__continue" to={paths.dashboard} onClick={dismissInstallPromotion}>
            Продолжить в браузере
          </Link>
        )}
        <p className="pwa-install__note">
          {installed
            ? 'Для занятий нужен интернет.'
            : 'Установка необязательна — всё работает и в браузере. Для занятий нужен интернет.'}
        </p>
      </div>
    </main>
  );
}
